export const ROUTES = {
  AUTH: {
    HOME: "/",
    LOGIN: "/login",
    LOGOUT: "/logout",
    REGISTRATION: "/registration",
    FORGOT_PASSWORD: "/forgot-password",
    RESET_PASSWORD: "/reset_password",
    SUCCESS_MSG: "/success_msg",
    SIGNUP: "/signup",
    SIGNUP_TWO: "/signup-two",
    SIGNUP_THREE: "/signup-three",
    SIGNUP_FOUR: "/signup-four",
    SIGNUP_FIVE: "/signup-five",
    SIGNUP_SIX: "/signup_six",
    VIDEO_CALL: "/video-call",
    PARTICIPANT_VIDEO_JOIN: "/participant-video-join",
  },

  GLOBAL: {
    HOME: "/home",
    REPORTS: "/reports",
    REPORTS_TYPE: "/reports/:reportType",
    CDM_FINDERS: "/cdm-finders",
    CDM_FINDERS_TYPE: "/cdm-finders/:reportType",
    RESULTS: "/results",
    IMMUNIZATIONS: "/immunizations",
    PATIENT: "/patient",
    DOCUMENTS: "/documents",
    APPOINTMENTS: "/appointments",
    PAYMENTS: "/payments",
    COMMUNICATIONS: "/communications",
  },

  PATIENT: {
    BASE: "/patients/:patientId",
    DETAILS: "/patients/:patientId/details",
    PREGNANCY: "/patients/:patientId/pregnancy",
    CDM: "/patients/:patientId/cdm",
    REFERRALS: "/patients/:patientId/referrals",
    LAB_ORDERS: "/patients/:patientId/lab-orders",
    DOCUMENTS: "/patients/:patientId/documents",
    COMMUNICATION: "/patients/:patientId/communication",
    PAYMENTS: "/patients/:patientId/payments",
    FAMILY: "/patients/:patientId/family",
    CONSULT: "/patients/:patientId/consult",
    CONSULTATIONS: "/patients/:patientId/consultations",
    RESULTS: "/patients/:patientId/results",
    IMMUNIZATIONS: "/patients/:patientId/immunizations",
    MEDICATION: "/patients/:patientId/medication",
    BASELINE: "/patients/:patientId/baseline",
    TASKS: "/patients/:patientId/tasks",
    CONSULTATION_EDIT: "/patients/:patientId/consultation/edit/:consultationId",
  },

  // Legacy patient paths (id in ?patientId= query). Kept only so already-sent
  // links (emails, SMS, bookmarks) redirect to the new /patients/:patientId/*.
  PATIENT_LEGACY: {
    BASE: "/patient",
    TAB: "/patient/:legacyTab",
    CONSULTATION_EDIT: "/patient/consultation/edit/:consultationId",
  },

  SETTINGS: {
    ROOT: "/settings/practice-details",
    USER_DETAILS: "/settings/user-details",
    PRACTICE_DETAILS: "/settings/practice-details",
    LOCATIONS: "/settings/locations",
    SERVICE_DETAILS: "/settings/service-details",
    DRUGS_FILE: "/settings/drugs-file",
    DOCUMENT_TYPES: "/settings/document-types",
    VACCINES: "/settings/vaccines",
    PRACTICE_HOURS: "/settings/practice-hours-details",
    SMS_SETTINGS: "/settings/sms-settings-details",
    CONSULTATION_TEMPLATES: "/settings/consultation-templates",
    LETTER_TEMPLATES: "/settings/letter-templates",
    PRACTICE_SETTINGS: "/settings/practice-settings",
    EMAIL_TEMPLATES: "/settings/email-templates",
    PAYMENTS_DETAILS: "/settings/payments-details",
    // Stripe-hosted onboarding redirect targets — same page/component as
    // PAYMENTS_DETAILS, distinguished only by the pathname suffix (see
    // PaymentDetailsPage: it refetches status + toasts, then navigates back
    // to PAYMENTS_DETAILS). Not standalone screens.
    PAYMENTS_DETAILS_ONBOARDING_COMPLETE:
      "/settings/payments-details/onboarding-complete",
    PAYMENTS_DETAILS_ONBOARDING_REFRESH:
      "/settings/payments-details/onboarding-refresh",
    HEADERS_FOOTERS: "/settings/headers-and-footers",
    HEALTHLINK: "/settings/healthlink-details",
    MOBILE_APP: "/settings/mobile-app",
    MERGE_PATIENTS: "/settings/merge-patients",
    PROFILE: "/settings/profile",
    SETTINGS: "/settings/settings",
  },
};

export const pathGenerators = {
  auth: {
    home: () => "/",
    login: () => "/login",
    logout: () => "/logout",
    registration: () => "/registration",
    forgotPassword: () => "/forgot-password",
    resetPassword: () => `/reset_password`,
    successMsg: () => "/success_msg",
    signup: () => "/signup",
    signupTwo: () => "/signup-two",
    signupThree: () => "/signup-three",
    signupFour: () => "/signup-four",
    signupFive: () => "/signup-five",
    signupSix: () => "/signup_six",
    videoCall: () => "/video-call",
    participantVideoJoin: () => "/participant-video-join",
  },

  global: {
    home: () => "/home",
    reports: () => "/reports",
    reportsType: ({ reportType }) => `/reports/${reportType}`,
    cdmFinders: ({ reportType }) => `/cdm-finders/${reportType}`,
    results: () => "/results",
    immunizations: () => "/immunizations",
    patient: () => "/patient",
    documents: () => "/documents",
    appointments: () => "/appointments",
    payments: () => "/payments",
    communications: () => "/communications",
  },

  // Every patient path takes the patient id — it lives in the URL, not storage.
  patient: {
    base: (patientId) => `/patients/${patientId}`,
    details: (patientId) => `/patients/${patientId}/details`,
    pregnancy: (patientId) => `/patients/${patientId}/pregnancy`,
    cdm: (patientId) => `/patients/${patientId}/cdm`,
    referrals: (patientId) => `/patients/${patientId}/referrals`,
    labOrders: (patientId) => `/patients/${patientId}/lab-orders`,
    documents: (patientId) => `/patients/${patientId}/documents`,
    communication: (patientId) => `/patients/${patientId}/communication`,
    payments: (patientId) => `/patients/${patientId}/payments`,
    family: (patientId) => `/patients/${patientId}/family`,
    consult: (patientId) => `/patients/${patientId}/consult`,
    consultations: (patientId) => `/patients/${patientId}/consultations`,
    results: (patientId) => `/patients/${patientId}/results`,
    immunizations: (patientId) => `/patients/${patientId}/immunizations`,
    medication: (patientId) => `/patients/${patientId}/medication`,
    baseline: (patientId) => `/patients/${patientId}/baseline`,
    tasks: (patientId) => `/patients/${patientId}/tasks`,
    consultationEdit: ({ patientId, consultationId }) =>
      `/patients/${patientId}/consultation/edit/${consultationId}`,
  },

  settings: {
    root: () => "/settings/practice-details",
    practiceDetails: () => "/settings/practice-details",
    locations: () => "/settings/locations",
    userDetails: () => "/settings/user-details",
    serviceDetails: () => "/settings/service-details",
    drugsFile: () => "/settings/drugs-file",
    documentTypes: () => "/settings/document-types",
    vaccines: () => "/settings/vaccines",
    practiceHours: () => "/settings/practice-hours-details",
    smsSettings: () => "/settings/sms-settings-details",
    consultationTemplates: () => "/settings/consultation-templates",
    letterTemplates: () => "/settings/letter-templates",
    practiceSettings: () => "/settings/practice-settings",
    emailTemplates: () => "/settings/email-templates",
    paymentsDetails: () => "/settings/payments-details",
    paymentsDetailsOnboardingComplete: () =>
      "/settings/payments-details/onboarding-complete",
    paymentsDetailsOnboardingRefresh: () =>
      "/settings/payments-details/onboarding-refresh",
    headersFooters: () => "/settings/headers-and-footers",
    healthlink: () => "/settings/healthlink-details",
    mobileApp: () => "/settings/mobile-app",
    mergePatients: () => "/settings/merge-patients",
    profile: () => "/settings/profile",
    settings: () => "/settings/settings",
    drugFavourites: () => "/settings/user-details?tab=drug-favourites",
  },
};

function toSegment(fullPath) {
  return fullPath.replace(/^\//, "");
}

export const ROUTE_SEGMENTS = {
  AUTH: Object.fromEntries(
    Object.entries(ROUTES.AUTH).map(([k, v]) => [
      k,
      toSegment(v.replace(/:token.*/, "")),
    ])
  ),
  GLOBAL: Object.fromEntries(
    Object.entries(ROUTES.GLOBAL).map(([k, v]) => [k, toSegment(v)])
  ),
  PATIENT: Object.fromEntries(
    Object.entries(ROUTES.PATIENT).map(([k, v]) => [k, toSegment(v)])
  ),
  SETTINGS: Object.fromEntries(
    Object.entries(ROUTES.SETTINGS).map(([k, v]) => [k, toSegment(v)])
  ),
  CONSULTATION_EDIT: toSegment(ROUTES.PATIENT.CONSULTATION_EDIT),
};

ROUTE_SEGMENTS.AUTH.RESET_PASSWORD = "reset_password";
ROUTE_SEGMENTS.GLOBAL.REPORTS_TYPE = "reports/:reportType";
ROUTE_SEGMENTS.GLOBAL.CDM_FINDERS_TYPE = "cdm-finders/:reportType";

// Tab segments relative to PATIENT.BASE ("patients/:patientId"), for nested
// <Route> children so :patientId sits on the parent (PatientLayout) route and is
// available via useParams() in the layout and every tab.
export const PATIENT_CHILD_SEGMENTS = Object.fromEntries(
  Object.entries(ROUTES.PATIENT)
    .filter(([key]) => key !== "BASE")
    .map(([key, path]) => [key, path.replace(ROUTES.PATIENT.BASE + "/", "")])
);
export const PATIENT_BASE_SEGMENT = toSegment(ROUTES.PATIENT.BASE);

const AUTH_PATHS = [
  ...Object.values(ROUTES.AUTH).filter((p) => !p.includes(":")),
  ROUTES.AUTH.RESET_PASSWORD.replace(":token", ""),
];
const PATIENT_PATH_PREFIX = "/patients/";
const LEGACY_PATIENT_PATH_PREFIX = "/patient/";

export function buildPath(pathTemplate, params = {}) {
  let result = pathTemplate;
  Object.entries(params).forEach(([key, value]) => {
    result = result.replace(`:${key}`, encodeURIComponent(value));
  });
  return result;
}

function getResetPasswordBase() {
  return ROUTES.AUTH.RESET_PASSWORD.replace(/:.*$/, "/");
}

export function isPublicRoute(pathname) {
  const normalized = pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  return (
    AUTH_PATHS.some(
      (p) => normalized === p || normalized === p.replace(/\/$/, "")
    ) || pathname.startsWith(getResetPasswordBase())
  );
}

export function isPatientRoute(pathname) {
  return (
    pathname.startsWith(PATIENT_PATH_PREFIX) ||
    pathname.startsWith(LEGACY_PATIENT_PATH_PREFIX)
  );
}

export function getPublicPaths() {
  return [...AUTH_PATHS, getResetPasswordBase()];
}
